import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSaltoACita } from "./saltoACita";

import HttpManager from "../../../services/HttpManager";
import { ProspectEvent } from "../../../services/v2/prospects.service";
import { conIcono, diaClave, diaEtiqueta, fechaDia, fechaLarga, hora, KIND_LABEL } from "./salesHelpers";
import NotaVoz from "./notaVoz";
import { abrirArchivo } from "../../../components/enrollment/visorArchivo";
import { mapaLink } from "./ubicacionModal";
import { formatoWa } from "./horariosModal";
import { BarraSeleccion, MarcaSeleccion, copiarTexto } from "./seleccion";
import { EmojisFlotante } from "./equipoEmojis";
import { guardarSticker, IconoSticker, StickerImg } from "./stickers";
import ReenviarClientes, { textoDe } from "./reenviarClientes";   // *negritas*, _cursivas_, ~tachado~ como los ve el cliente en WhatsApp

interface Props {
	campusId: number;
	prospectId: number;
	events: ProspectEvent[];      // del más viejo al más nuevo
	hayMas: boolean;
	onMas: () => void;
	cargando: boolean;
	/** WhatsApp: responder a un mensaje específico y reaccionar con emoji (la API de Meta lo permite; Woztell no). */
	onResponder?: (ev: ProspectEvent) => void;
	onReaccionar?: (ev: ProspectEvent, emoji: string) => void;
	/** Nombre del cliente para la cita ("Respondiendo a María"). */
	nombreCliente?: string;
	/** Ir a este evento y resaltarlo (resultado de buscar); n cambia en cada salto. */
	salto?: { id: number; n: number } | null;
}

const TICKS: { [k: string]: { t: string; label: string } } = {
	enviado: { t: "✓", label: "Enviado" }, entregado: { t: "✓✓", label: "Entregado" },
	leido: { t: "✓✓", label: "Leído" }, fallido: { t: "!", label: "No se entregó" },
};

/** Foto en la burbuja; al tocarla se abre grande encima de la conversación (no en otra pestaña). Esc o clic afuera la cierra. */
const FotoConVisor: React.FC<{ url: string }> = ({ url }) => {
	const [abierta, setAbierta] = useState(false);
	useEffect(() => {
		if (!abierta) return;
		const k = (e: KeyboardEvent) => { if (e.key === "Escape") setAbierta(false); };
		document.addEventListener("keydown", k);
		return () => document.removeEventListener("keydown", k);
	}, [abierta]);
	return (
		<>
			<button type="button" className="cv-foto-btn" onClick={() => setAbierta(true)} aria-label="Ver foto grande">
				<img className="cv-foto" src={url} alt="Foto que mandó" />
			</button>
			{abierta ? (
				<div className="cv-visor" role="dialog" aria-label="Foto" onClick={() => setAbierta(false)}>
					<img src={url} alt="Foto que mandó" onClick={(e) => e.stopPropagation()} />
					<button type="button" className="cv-visor-cerrar" onClick={() => setAbierta(false)} aria-label="Cerrar">×</button>
				</div>
			) : null}
		</>
	);
};

/** Anuncio de clic a WhatsApp del que llegó (como lo muestra Woztell): miniatura, título, texto y liga al anuncio. */
const TarjetaAnuncio: React.FC<{ r: any }> = ({ r }) => {
	const red = String(r.source_url || "").toLowerCase().includes("instagram") ? "Instagram" : "Facebook";
	return (
		<a className="cv-anuncio" href={r.source_url || r.video_url || "#"} target="_blank" rel="noreferrer">
			{r.thumbnail_url || r.image_url ? <img src={r.thumbnail_url || r.image_url} alt="" /> : null}
			<span className="cv-anuncio-tx">
				<span className="cv-anuncio-de">Respondió a un anuncio · {red}</span>
				{r.headline ? <b>{r.headline}</b> : null}
				{r.body ? <span className="cv-anuncio-body">{r.body}</span> : null}
			</span>
		</a>
	);
};

/**
 * Foto, audio o documento de WhatsApp: el archivo vive en Spaces privado y se pide una URL temporal.
 * Fotos, stickers y audios se piden solos al aparecer en pantalla, como en WhatsApp (Esteban, 2026-10-02:
 * "le tuve que dar ver foto para ver la foto"); videos y documentos siguen con botón para no bajar de más.
 */
const Adjunto: React.FC<{ campusId: number; prospectId: number; ev: ProspectEvent }> = ({ campusId, prospectId, ev }) => {
	const [url, setUrl] = useState<string | null>(null);
	const [err, setErr] = useState<string | null>(null);
	const caja = useRef<HTMLSpanElement>(null);
	const m = ev.meta || {};
	// Una plantilla con foto, video o PDF arriba guarda type "template": el tipo real sale del archivo.
	const tipo = m.type === "template" && m.mime ? (String(m.mime).startsWith("image/") ? "image" : String(m.mime).startsWith("video/") ? "video" : "document") : String(m.type || "");
	// Videos también: con preload="metadata" solo baja el primer cuadro (la miniatura), no el video entero.
	const solo = !m.url && !!m.file && (tipo === "image" || tipo === "sticker" || tipo === "video");
	const verDocumento = (u: string) => abrirArchivo({ origen: "WappChat", nombre: m.filename || "Documento", mime: m.mime,
		cargar: async () => ({ blob: await (await fetch(u)).blob(), nombre: m.filename }) });
	const abrir = async () => {
		try {
			const r = await HttpManager.getInstance().prospectsService.media(campusId, prospectId, ev.id);
			if (!r.data.url) { setErr("No disponible"); return; }
			setUrl(r.data.url);
			// Documento: con el mismo toque se abre en el visor (antes había que tocar dos veces).
			if (!solo && tipo !== "audio") verDocumento(r.data.url);
		} catch (e) { setErr("No se pudo abrir"); }
	};
	useEffect(() => {
		const el = caja.current;
		if (!solo || url || err || !el) return;
		if (typeof IntersectionObserver === "undefined") { abrir(); return; }
		const io = new IntersectionObserver((vistos) => {
			if (vistos.some((v) => v.isIntersecting)) { io.disconnect(); abrir(); }
		}, { rootMargin: "300px" });
		io.observe(el);
		return () => io.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [solo, ev.id]);

	if (m.url) {   // lo que mandó Woztell: link público de su almacenamiento
		return <a className="cv-adjunto" href={m.url} target="_blank" rel="noreferrer">{m.filename || "Abrir archivo"}</a>;
	}
	if (!m.file) {
		return m.woztell_file ? <span className="cv-adjunto apagado">Archivo guardado en Woztell (no disponible)</span> : null;
	}
	// Audios: reproductor propio que baja el archivo completo (la URL temporal vencía y la nota se cortaba).
	if (tipo === "audio") return <NotaVoz campusId={campusId} prospectId={prospectId} eventId={ev.id} mime={m.mime} duracion={m.duration} />;
	if (!url) {
		if (solo && !err) return tipo === "sticker" ? <span ref={caja}><StickerImg url={null} /></span> : <span ref={caja} className="cv-adjunto apagado">{tipo === "video" ? "Cargando video…" : "Cargando foto…"}</span>;
		return <button className="cv-adjunto" onClick={abrir}>{err || (tipo === "audio" ? "Escuchar audio" : tipo === "image" || tipo === "sticker" ? "Ver foto" : tipo === "video" ? "Ver video" : "Abrir documento")}</button>;
	}
	if (tipo === "sticker") return <StickerImg url={url} />;
	if (tipo === "image") return <FotoConVisor url={url} />;
	if (tipo === "video") return <video className="cv-foto cv-video" controlsList="nodownload" controls playsInline preload="metadata" src={url + "#t=0.001"} />;
	// En el visor de WAPP (no otra pestaña) y con la regla de descargas: guardar en WappDrive salvo quien puede descargar.
	return <button className="cv-adjunto" onClick={() => verDocumento(url)}>{m.filename || "Abrir documento"}</button>;
};

/**
 * La conversación (lienzo del 19-sep): WhatsApp en burbujas y, en la misma línea de tiempo, las notas, llamadas,
 * tareas y movimientos. Lo más nuevo abajo, como en WhatsApp.
 */
const REACCIONES = ["👍", "❤️", "😂", "😮", "😢", "🙏"];
/** Sticker con copia en Spaces (lo que llegó por Woztell trae liga externa y no se puede guardar). */
const esStickerGuardable = (ev: ProspectEvent) => ev.meta?.type === "sticker" && !!ev.meta?.file && !/^https?:/.test(String(ev.meta.file)) && !ev.meta?.eliminado;

/** Palomitas como WhatsApp: ✓ enviado, ✓✓ entregado, ✓✓ azules leído, ! no se entregó. */
const Palomitas: React.FC<{ estado: string; label: string }> = ({ estado, label }) => (
	<span className={"cv-tick " + estado} title={label} aria-label={label}>
		{estado === "fallido" ? (
			<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><circle cx="8" cy="8" r="7" fill="currentColor" /><path d="M8 4v5M8 11.2v.6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>
		) : estado === "enviado" ? (
			<svg viewBox="0 0 16 11" width="15" height="11" aria-hidden="true"><path d="M1.5 6l3.5 3.5L14 1" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
		) : (
			<svg viewBox="0 0 20 11" width="18" height="11" aria-hidden="true"><path d="M1 6l3.5 3.5L13 1M8.5 8.5l1 1L18 1" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
		)}
	</span>
);

/** Mensaje en camino (todavía sin ✓), como el relojito de WhatsApp. */
const Reloj = () => (
	<span className="cv-tick" title="Enviando" aria-label="Enviando">
		<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><circle cx="8" cy="8" r="6.3" fill="none" stroke="currentColor" strokeWidth="1.4" /><path d="M8 4.6V8l2.3 1.4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
	</span>
);

const IcoProhibido = () => (
	<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" style={{ verticalAlign: "-2px" }}>
		<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M5.6 5.6l12.8 12.8" stroke="currentColor" strokeWidth="2" />
	</svg>
);
const IcoCara = () => <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" /><circle cx="9" cy="10" r="1.2" fill="currentColor" /><circle cx="15" cy="10" r="1.2" fill="currentColor" /><path d="M8.2 14.2a4.5 4.5 0 0 0 7.6 0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>;
const IcoResponder = () => <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M10 7 4 12l6 5v-3.2c4.4 0 7.4 1.3 10 4.7-1-4.8-4-8.6-10-9.4V7z" fill="currentColor" /></svg>;
const IcoCopiar = () => <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M8 3h11a2 2 0 0 1 2 2v12h-2V5H8V3zM4 7h11a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zm0 2v11h11V9H4z" /></svg>;
const IcoMarcar = () => <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16zm-1.2 11.6-3.4-3.4 1.4-1.4 2 2 4.6-4.6 1.4 1.4z" /></svg>;

/**
 * Responder y reaccionar como WhatsApp (Esteban, 2026-10-05: "no se entiende, parece que lo mandó el cliente").
 * Computadora: al pasar el mouse aparecen dos botones redondos FUERA de la burbuja. Teléfono: mantener presionado abre
 * las reacciones y "Responder"; deslizar la burbuja a la derecha también responde.
 */
type Menu = { ev: ProspectEvent; movil: boolean } | null;

const Conversacion: React.FC<Props> = ({ campusId, prospectId, events, hayMas, onMas, cargando, onResponder, onReaccionar, nombreCliente, salto }) => {
	const [menu, setMenu] = React.useState<Menu>(null);
	// Selección múltiple como WhatsApp (Esteban, 2026-10-12): Copiar el texto de todos y Reenviar a otros clientes.
	const [sel, setSel] = React.useState<number[] | null>(null);
	const [reenviar, setReenviar] = React.useState<ProspectEvent[] | null>(null);
	useEffect(() => { setSel(null); }, [prospectId]);
	const alternarSel = (id: number) => setSel((prev) => (prev || []).includes(id) ? (prev || []).filter((x) => x !== id) : [...(prev || []), id]);
	const elegidosSel = (): ProspectEvent[] => events.filter((e) => (sel || []).includes(e.id));
	const toque = useRef<{ x: number; y: number; t: any; id: number; dx: number; el: HTMLElement | null } | null>(null);
	const interactivo = !!(onResponder || onReaccionar);
	const gestos = (ev: ProspectEvent) => (!interactivo || !ev.external_id || sel ? {} : {
		onTouchStart: (e: React.TouchEvent<HTMLDivElement>) => {
			const t0 = e.touches[0];
			const el = e.currentTarget;
			const t = setTimeout(() => { if (toque.current?.id === ev.id && Math.abs(toque.current.dx) < 10) { try { (navigator as any).vibrate?.(12); } catch { /* sin vibración */ } setMenu({ ev, movil: true }); toque.current = null; } }, 450);
			toque.current = { x: t0.clientX, y: t0.clientY, t, id: ev.id, dx: 0, el };
		},
		onTouchMove: (e: React.TouchEvent<HTMLDivElement>) => {
			const c = toque.current;
			if (!c) return;
			const dx = e.touches[0].clientX - c.x, dy = e.touches[0].clientY - c.y;
			if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) { clearTimeout(c.t); if (c.el) c.el.style.transform = ""; toque.current = null; return; }
			c.dx = dx;
			if (Math.abs(dx) > 8) clearTimeout(c.t);
			if (dx > 0 && onResponder && c.el) c.el.style.transform = `translateX(${Math.min(dx, 80)}px)`;
		},
		onTouchEnd: () => {
			const c = toque.current;
			if (!c) return;
			clearTimeout(c.t);
			if (c.el) { c.el.style.transition = "transform .18s"; c.el.style.transform = ""; const el = c.el; setTimeout(() => { el.style.transition = ""; }, 200); }
			if (c.dx > 60 && onResponder) { try { (navigator as any).vibrate?.(8); } catch { /* sin vibración */ } onResponder(ev); }
			toque.current = null;
		},
		onContextMenu: (e: React.MouseEvent) => { if (window.matchMedia("(max-width: 700px)").matches) e.preventDefault(); },
	});
	useEffect(() => {
		if (!menu) return;
		const k = (e: KeyboardEvent) => { if (e.key === "Escape") setMenu(null); };
		document.addEventListener("keydown", k);
		return () => document.removeEventListener("keydown", k);
	}, [menu]);
	// "+" de la pastilla: cualquier emoji para reaccionar, como WhatsApp.
	const [masReac, setMasReac] = useState<ProspectEvent | null>(null);
	const guardar = (ev: ProspectEvent) => guardarSticker(() => HttpManager.getInstance().prospectsService.whatsappGuardarSticker(campusId, prospectId, ev.id));
	const reaccionar = (ev: ProspectEvent, e: string) => { setMenu(null); if (onReaccionar) onReaccionar(ev, ev.meta?.reaccion_nuestra === e ? "" : e); };
	const pastilla = (ev: ProspectEvent) => (
		<div className="cv-pastilla" role="group" aria-label="Reaccionar">
			{REACCIONES.map((e) => <button key={e} className={ev.meta?.reaccion_nuestra === e ? "on" : ""} onClick={() => reaccionar(ev, e)} aria-label={"Reaccionar " + e}>{e}</button>)}
			<button className="eqe-mas" onClick={() => { setMenu(null); setMasReac(ev); }} aria-label="Más emojis" title="Más emojis">+</button>
		</div>
	);
	const caja = useRef<HTMLDivElement>(null);
	const alto = useRef<number>(0);
	const primero = useRef<number | null>(null);
	const ultimo = useRef<number | null>(null);

	// Al abrir o al llegar algo nuevo: hasta abajo. Al cargar anteriores: se queda donde estaba. Si solo cambiaron
	// palomitas o reacciones, no se mueve (antes brincaba hasta abajo con cada ✓✓ y soltaba la barra de la nota de voz);
	// y si la persona subió a leer, un mensaje del cliente no la jala hasta abajo (uno nuestro sí, como WhatsApp).
	useLayoutEffect(() => {
		const el = caja.current;
		if (!el) return;
		const primerId = events.length ? events[0].id : null;
		const ult = events.length ? events[events.length - 1] : null;
		const ultId = ult ? ult.id : null;
		const cercaDeAbajo = alto.current - el.scrollTop - el.clientHeight < 160;
		if (primero.current !== null && primerId !== primero.current && alto.current) {
			el.scrollTop = el.scrollHeight - alto.current;
		} else if (ultimo.current === null || (ultId !== ultimo.current && (cercaDeAbajo || ult?.direction === "out"))) {
			el.scrollTop = el.scrollHeight;
		}
		primero.current = primerId;
		ultimo.current = ultId;
		alto.current = el.scrollHeight;
	}, [events]);

	useEffect(() => { primero.current = null; ultimo.current = null; alto.current = 0; }, [prospectId]);

	// Tocar la cita lleva al mensaje original y lo resalta (si es viejo, carga anteriores).
	const irACita = useSaltoACita(caja, "data-ev", events, hayMas && !cargando, onMas);
	useEffect(() => { if (salto) irACita(salto.id); }, [salto?.n]);

	let ultimoDia = -1;
	return (
		<div className={"cv-chat" + (sel ? " eligiendo" : "")} ref={caja}>
			{hayMas ? <div className="cv-mas"><button className="btn-wa" disabled={cargando} onClick={onMas}>{cargando ? "Cargando…" : "Ver anteriores"}</button></div> : null}
			{!events.length && !cargando ? <div className="ventas-vacio">Sin mensajes ni notas todavía.</div> : null}
			{events.map((ev) => {
				const dia = diaClave(ev.occurred_at);
				const sep = dia !== ultimoDia ? <div className="cv-dia" key={"d" + ev.id}>{diaEtiqueta(ev.occurred_at)}</div> : null;
				ultimoDia = dia;
				if (ev.kind === "whatsapp") {
					const sale = ev.direction === "out";
					const enCamino = !!ev.meta?.enCamino;   // recién mandado desde aquí, todavía sin respuesta del servidor
					const tick = sale && ev.wa_status ? TICKS[ev.wa_status] : null;
					return (
						<React.Fragment key={ev.id}>
							{sep}
							<div data-ev={ev.id} className={"cv-msg " + (sale ? "sale" : "entra") + (menu?.ev.id === ev.id ? " elegido" : "") + (sel?.includes(ev.id) ? " sel" : "")}
								onClickCapture={sel && !ev.meta?.eliminado && !enCamino ? (e) => { e.preventDefault(); e.stopPropagation(); alternarSel(ev.id); } : undefined}>
								{sel ? <MarcaSeleccion on={sel.includes(ev.id)} /> : null}
								<div className={"cv-burbuja " + (sale ? "sale" : "entra") + (ev.wa_status === "fallido" ? " fallido" : "") + (ev.meta?.type === "sticker" && ev.meta?.file && !ev.meta?.eliminado ? " sticker" : "")} title={fechaLarga(ev.occurred_at)} {...gestos(ev)}>
									{(ev as any).cita ? (
										<div className={"cv-cita " + ((ev as any).cita.nuestro ? "nuestra" : "suya")} role="button" tabIndex={0} title="Ir al mensaje"
											onClick={(e) => { e.stopPropagation(); irACita((ev as any).cita.id); }}
											onKeyDown={(e) => { if (e.key === "Enter") irACita((ev as any).cita.id); }}>
											<b>{(ev as any).cita.nuestro ? "Nosotros" + ((ev as any).cita.de ? " · " + (ev as any).cita.de.split(" ")[0] : "") : nombreCliente || "Cliente"}</b>
											<span>{(ev as any).cita.body || "Archivo"}</span>
										</div>
									) : null}
									{ev.meta?.template ? <div className="cv-plantilla">Plantilla · {ev.meta.template}</div> : null}
									{/* La foto, video o PDF de la plantilla va arriba del texto, como lo ve el cliente. */}
									{ev.meta?.type === "template" && ev.meta?.file ? <Adjunto campusId={campusId} prospectId={prospectId} ev={ev} /> : null}
									{!sale && ev.meta?.referral?.source_type === "ad" ? <TarjetaAnuncio r={ev.meta.referral} /> : null}
									{/* "[Nota de voz]", "[Foto]"…: con el archivo a la vista sobra el texto de relleno. */}
									{ev.meta?.eliminado ? (
										/* Lo borró el cliente "para todos": como en WhatsApp, sin el texto (queda guardado en meta.eliminado_texto). */
										<p className="cv-eliminado"><IcoProhibido /> Se eliminó este mensaje</p>
									) : ev.meta?.type === "location" && ev.meta?.lat != null ? (
										/* Pin de mapa (mandado desde Ubicación o el que comparte el cliente): se abre en Google Maps. */
										<a className="cv-ubicacion" href={mapaLink({ lat: Number(ev.meta.lat), lng: Number(ev.meta.lng) })} target="_blank" rel="noreferrer">
											<span className="cv-ubicacion-pin" aria-hidden="true"><svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" /></svg></span>
											<span className="cv-ubicacion-tx"><b>{ev.meta.name || "Ubicación"}</b>{ev.meta.address ? <span>{ev.meta.address}</span> : null}<em>Ver en Google Maps</em></span>
										</a>
									) : ev.body && !(ev.meta?.file && /^\[[^\]]*\]$/.test(ev.body)) ? <p>{/^\[(Mensaje unsupported|Unsupported_message)\]$/i.test(ev.body) ? <span className="cv-no-disp">⚠️ WhatsApp no deja ver este mensaje aquí. Revísalo en el teléfono.</span> : formatoWa(conIcono(ev.body))}</p> : null}
									{ev.meta?.eliminado || (ev.meta?.type === "template" && ev.meta?.file) ? null : <Adjunto campusId={campusId} prospectId={prospectId} ev={ev} />}
									{Array.isArray(ev.meta?.botones) && ev.meta.botones.length > 0 ? (
										/* Botones de respuesta rápida que traía la plantilla: lo que el cliente podía tocar. */
										<div className="cv-botones">{ev.meta.botones.map((b: string, i: number) => <span key={i}>{b}</span>)}</div>
									) : null}
									{ev.meta?.editado_at && ev.meta?.antes && !ev.meta?.eliminado ? (
										<details className="cv-antes"><summary>Ver lo que decía antes</summary><span>{ev.meta.antes}</span></details>
									) : null}
									<span className="cv-pie">
										{ev.meta?.editado_at && !ev.meta?.eliminado ? <span className="cv-editado">Editado · </span> : null}
										{hora(ev.occurred_at)}{sale && ev.author_name ? " · " + ev.author_name.split(" ")[0] : ""}
										{tick ? <Palomitas estado={ev.wa_status as string} label={tick.label} /> : enCamino ? <Reloj /> : null}
									</span>
									{ev.wa_status === "fallido" && ev.meta?.error ? <span className="cv-error">No se entregó: {ev.meta.error}</span> : null}
									{ev.meta?.reaccion_cliente || ev.meta?.reaccion_nuestra ? (
										<span className="cv-reacciones" title={[ev.meta?.reaccion_cliente ? (nombreCliente || "El cliente") + " reaccionó " + ev.meta.reaccion_cliente : "", ev.meta?.reaccion_nuestra ? "Reaccionamos " + ev.meta.reaccion_nuestra : ""].filter(Boolean).join(" · ")}>
											{ev.meta?.reaccion_cliente ? <span>{ev.meta.reaccion_cliente}</span> : null}
											{ev.meta?.reaccion_nuestra ? <span className="nuestra">{ev.meta.reaccion_nuestra}</span> : null}
										</span>
									) : null}
								</div>
								{interactivo && !ev.meta?.eliminado && !enCamino ? (
									<div className="cv-flot">
										{ev.external_id && onReaccionar ? <button title="Reaccionar" aria-label="Reaccionar" onClick={() => setMenu(menu?.ev.id === ev.id ? null : { ev, movil: false })}><IcoCara /></button> : null}
										{ev.external_id && onResponder ? <button title="Responder" aria-label="Responder" onClick={() => onResponder(ev)}><IcoResponder /></button> : null}
										{textoDe(ev) ? <button title="Copiar" aria-label="Copiar" onClick={() => copiarTexto(textoDe(ev))}><IcoCopiar /></button> : null}
										{esStickerGuardable(ev) ? <button title="Guardar en Mis Stickers" aria-label="Guardar en Mis Stickers" onClick={() => guardar(ev)}><IconoSticker /></button> : null}
										<button title="Seleccionar" aria-label="Seleccionar" onClick={() => { setMenu(null); setSel([ev.id]); }}><IcoMarcar /></button>
									</div>
								) : null}
								{menu && !menu.movil && menu.ev.id === ev.id && onReaccionar ? <><div className="wl-velo" onClick={() => setMenu(null)} />{pastilla(ev)}</> : null}
							</div>
						</React.Fragment>
					);
				}
				if (ev.kind === "nota" || ev.kind === "llamada" || ev.kind === "tarea") {
					return (
						<React.Fragment key={ev.id}>
							{sep}
							<div data-ev={ev.id} className={"cv-tarjeta " + ev.kind}>
								<div className="cv-tarjeta-cab">
									<span className="t">{KIND_LABEL[ev.kind]}{ev.kind === "llamada" && ev.direction === "in" ? " entrante" : ""}</span>
									<span className="meta">{hora(ev.occurred_at)}{ev.author_name ? " · " + ev.author_name : ""}</span>
									{ev.kind !== "llamada" ? <span className="solo-equipo">Solo equipo · el cliente no lo ve</span> : null}
								</div>
								{ev.kind === "nota" && ev.meta?.titulo && !(ev.body || "").startsWith(ev.meta.titulo) ? <b className="cv-nota-tit">{ev.meta.titulo}</b> : null}
								{ev.body ? <p>{ev.body}</p> : null}
								{ev.kind === "tarea" && ev.meta?.status ? <div className="meta">{ev.meta.status}{ev.meta.due ? " · vence " + fechaDia(ev.meta.due) : ""}</div> : null}
							</div>
						</React.Fragment>
					);
				}
				return (
					<React.Fragment key={ev.id}>
						{sep}
						<div data-ev={ev.id} className={"cv-movimiento " + ev.kind}>
							<span>{ev.body}</span>
							<span className="meta">{hora(ev.occurred_at)}{ev.author_name ? " · " + ev.author_name : ""}</span>
						</div>
					</React.Fragment>
				);
			})}
			{menu && menu.movil ? (
				<div className="cv-hoja-velo" onClick={() => setMenu(null)}>
					<div className="cv-hoja" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Acciones del mensaje">
						<div className="cv-hoja-msg">{menu.ev.body && !/^\[[^\]]*\]$/.test(menu.ev.body) ? menu.ev.body : "Archivo"}</div>
						{onReaccionar ? pastilla(menu.ev) : null}
						<div className="cv-hoja-acc">
							{onResponder ? <button onClick={() => { const ev = menu.ev; setMenu(null); onResponder(ev); }}><IcoResponder /> Responder</button> : null}
							{textoDe(menu.ev) ? <button onClick={() => { copiarTexto(textoDe(menu.ev)); setMenu(null); }}><IcoCopiar /> Copiar</button> : null}
							{esStickerGuardable(menu.ev) ? <button onClick={() => { const ev = menu.ev; setMenu(null); guardar(ev); }}><IconoSticker /> Guardar Sticker</button> : null}
							<button onClick={() => { const id = menu.ev.id; setMenu(null); setSel([id]); }}><IcoMarcar /> Seleccionar</button>
						</div>
					</div>
				</div>
			) : null}
			{sel ? (
				<div className="cv-sel-barra">
					<BarraSeleccion n={sel.length} onCancelar={() => setSel(null)}
						texto={() => elegidosSel().map(textoDe).filter(Boolean).join("\n")}
						onReenviar={() => { const l = elegidosSel(); if (l.length) setReenviar(l); }} />
				</div>
			) : null}
			{masReac && onReaccionar ? <EmojisFlotante onElegir={(e) => { const ev = masReac; setMasReac(null); reaccionar(ev, e); }} onCerrar={() => setMasReac(null)} /> : null}
			{reenviar ? <ReenviarClientes campusId={campusId} prospectId={prospectId} eventos={reenviar} onCerrar={() => { setReenviar(null); setSel(null); }} /> : null}
		</div>
	);
};

export default Conversacion;
